import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, authedFetch } from '../hooks/useAuth';

type PickResult = {
  main_matches: number;
  bonus_match: boolean;
  prize_tier: string;
  checked_at: string;
  winning_numbers: number[];
  winning_bonus: number | null;
  matched_numbers: number[];
  missed_numbers: number[];
};

type Pick = {
  id: string;
  game: string;
  draw_date: string;
  numbers: number[];
  bonus_ball: number | null;
  strategy: string;
  created_at: string;
  result: PickResult | null;
};

type PicksPage = {
  items: Pick[];
  total: number;
  page: number;
  page_size: number;
};

type StrategyPerformance = {
  strategy: string;
  plays: number;
  evaluated: number;
  avg_matches: number;
  best_matches: number;
  prize_wins: number;
};

type NumberStat = { number: number; hits: number; plays: number };

type PersonalisationInsight = {
  active: boolean;
  game: string | null;
  evaluated_picks: number;
  min_required: number;
  favoured: number[];
  down_weighted: number[];
};

type Insights = {
  total_evaluated: number;
  per_strategy: StrategyPerformance[];
  top_hit: NumberStat[];
  top_missed: NumberStat[];
  personalisation: PersonalisationInsight;
};

function tierColor(tier: string): string {
  if (tier.startsWith('Division 1') || tier.startsWith('Division 2') || tier.startsWith('Division 3'))
    return 'text-green-700 bg-green-50 border-green-200';
  if (tier.startsWith('Division 4') || tier.startsWith('Division 5'))
    return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-slate-500 bg-slate-50 border-slate-200';
}

const fmtStrategy = (s: string) => s.replace(/_/g, ' ');

function Ball({ value, tone }: { value: number; tone: 'hit' | 'miss' | 'bonus' | 'plain' }) {
  const cls = {
    hit: 'bg-green-600 text-white ring-2 ring-green-300',
    miss: 'bg-slate-200 text-slate-500',
    bonus: 'bg-amber-400 text-white',
    plain: 'bg-sky-600 text-white',
  }[tone];
  return (
    <span className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${cls}`}>
      {value}
    </span>
  );
}

function InsightsPanel({ insights }: { insights: Insights }) {
  const { per_strategy, top_hit, top_missed, personalisation: p } = insights;

  if (insights.total_evaluated === 0) {
    return (
      <div className="mb-6 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4 text-sm text-slate-600">
        <p className="font-semibold text-slate-800">What the AI has learned</p>
        <p className="mt-1 text-slate-500">
          No picks have been evaluated yet. Once your saved picks are matched against their draws,
          you'll see hit/miss numbers, per-strategy performance, and the adjustments the model learns
          for you here.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-4 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-slate-800">What the AI has learned</p>
        <span className="text-xs text-slate-500">{insights.total_evaluated} evaluated</span>
      </div>

      {/* Most hit / most missed */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl bg-white/70 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-green-700">Most hit</p>
          {top_hit.length ? (
            <div className="flex flex-wrap gap-1.5">
              {top_hit.map((s) => (
                <span key={s.number} className="flex items-center gap-1">
                  <Ball value={s.number} tone="hit" />
                  <span className="text-[10px] text-slate-500">{s.hits}/{s.plays}</span>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400">No hits yet.</p>
          )}
        </div>
        <div className="rounded-xl bg-white/70 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Most missed</p>
          {top_missed.length ? (
            <div className="flex flex-wrap gap-1.5">
              {top_missed.map((s) => (
                <span key={s.number} className="flex items-center gap-1">
                  <Ball value={s.number} tone="miss" />
                  <span className="text-[10px] text-slate-500">{s.plays - s.hits}/{s.plays}</span>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400">Nothing notable.</p>
          )}
        </div>
      </div>

      {/* Personalisation */}
      <div className="rounded-xl bg-white/70 p-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">Personalisation</p>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              p.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
            }`}
          >
            {p.active ? 'Active' : 'Warming up'}
          </span>
        </div>
        {p.active ? (
          <div className="mt-2 space-y-2 text-xs text-slate-600">
            <p>
              Tuned to your <span className="font-semibold">{fmtStrategy(p.game ?? '')}</span> results
              ({p.evaluated_picks} evaluated picks).
            </p>
            {p.favoured.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-slate-500">Now favours:</span>
                {p.favoured.map((n) => <Ball key={n} value={n} tone="plain" />)}
              </div>
            )}
            {p.down_weighted.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-slate-500">Down-weights:</span>
                {p.down_weighted.map((n) => <Ball key={n} value={n} tone="miss" />)}
              </div>
            )}
            {p.favoured.length === 0 && p.down_weighted.length === 0 && (
              <p className="text-slate-500">
                No strong number preferences yet — your results are evenly spread, so the model isn't
                favouring any numbers.
              </p>
            )}
          </div>
        ) : (
          <p className="mt-1 text-xs text-slate-500">
            Needs {p.min_required} evaluated picks{p.game ? ` for ${fmtStrategy(p.game)}` : ''} before the
            model starts tuning your numbers ({p.evaluated_picks}/{p.min_required} so far).
          </p>
        )}
      </div>

      {/* Per-strategy performance */}
      {per_strategy.length > 0 && (
        <div className="rounded-xl bg-white/70 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">By strategy</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="text-slate-400">
                  <th className="py-1 pr-3 font-medium">Strategy</th>
                  <th className="py-1 pr-3 font-medium">Plays</th>
                  <th className="py-1 pr-3 font-medium">Avg</th>
                  <th className="py-1 pr-3 font-medium">Best</th>
                  <th className="py-1 font-medium">Wins</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {per_strategy.map((s) => (
                  <tr key={s.strategy} className="border-t border-slate-100">
                    <td className="py-1 pr-3 capitalize">{fmtStrategy(s.strategy)}</td>
                    <td className="py-1 pr-3">{s.evaluated}/{s.plays}</td>
                    <td className="py-1 pr-3">{s.avg_matches}</td>
                    <td className="py-1 pr-3">{s.best_matches}</td>
                    <td className="py-1">{s.prize_wins}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PickHistory() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [picks, setPicks] = useState<Pick[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [insights, setInsights] = useState<Insights | null>(null);

  useEffect(() => {
    if (!user) return;
    setFetching(true);
    authedFetch(`/api/v1/picks?page=${page}&page_size=10`)
      .then((r) => r.ok ? r.json() : null)
      .then((d: PicksPage | null) => {
        if (d) { setPicks(d.items); setTotal(d.total); }
        setFetching(false);
      })
      .catch(() => setFetching(false));
  }, [user, page]);

  // Insights are user-wide (not paginated) — fetch once when the user loads.
  useEffect(() => {
    if (!user) return;
    authedFetch('/api/v1/picks/insights')
      .then((r) => r.ok ? r.json() : null)
      .then((d: Insights | null) => { if (d) setInsights(d); })
      .catch(() => { /* insights are non-critical — fail silently */ });
  }, [user]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center"><p className="text-slate-500">Loading…</p></div>;
  }

  if (!user) {
    nav('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
      <div className="mx-auto max-w-2xl">
        <Link to="/profile" className="mb-6 block text-xs text-slate-400 hover:text-slate-600">← Profile</Link>
        <h1 className="mb-4 text-2xl font-bold text-slate-900">Pick History</h1>

        {insights && <InsightsPanel insights={insights} />}

        {fetching && <p className="text-sm text-slate-500">Loading picks…</p>}

        {!fetching && picks.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <p className="text-slate-500">No picks saved yet.</p>
            <p className="mt-1 text-sm text-slate-400">Generate numbers and tap "I played this" to track a bet.</p>
            <Link to="/" className="mt-4 inline-block rounded-full bg-sky-600 px-6 py-2 text-sm font-semibold text-white">
              Generate picks
            </Link>
          </div>
        )}

        <div className="space-y-3">
          {picks.map((pick) => {
            const r = pick.result;
            const winningSet = new Set(r?.winning_numbers ?? []);
            return (
              <div key={pick.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{pick.game.replace(/_/g, ' ')}</span>
                    <p className="text-sm text-slate-600">{pick.draw_date} · {fmtStrategy(pick.strategy)}</p>
                  </div>
                  {r ? (
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tierColor(r.prize_tier)}`}>
                      {r.prize_tier}
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-500">Pending</span>
                  )}
                </div>

                {/* The user's pick — green = hit, grey = missed once evaluated */}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {pick.numbers.map((n) => (
                    <Ball key={n} value={n} tone={r ? (winningSet.has(n) ? 'hit' : 'miss') : 'plain'} />
                  ))}
                  {pick.bonus_ball !== null && (
                    <Ball value={pick.bonus_ball} tone="bonus" />
                  )}
                </div>

                {r && (
                  <>
                    <p className="mt-2 text-xs text-slate-500">
                      {r.main_matches} main match{r.main_matches !== 1 ? 'es' : ''}
                      {r.bonus_match ? ' + bonus' : ''}
                    </p>
                    {r.winning_numbers.length > 0 && (
                      <div className="mt-2 border-t border-slate-100 pt-2">
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                          Winning draw
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {r.winning_numbers.map((n) => (
                            <Ball key={n} value={n} tone={pick.numbers.includes(n) ? 'hit' : 'plain'} />
                          ))}
                          {r.winning_bonus !== null && <Ball value={r.winning_bonus} tone="bonus" />}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {total > 10 && (
          <div className="mt-6 flex justify-center gap-3">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm disabled:opacity-40"
            >
              Previous
            </button>
            <span className="py-2 text-sm text-slate-500">Page {page} of {Math.ceil(total / 10)}</span>
            <button
              disabled={page * 10 >= total}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
